import { computed, defineAsyncComponent, ref } from "vue";
import type { DatePeriod } from "@/lib/datePresets";
import { formatLocalDateOnly } from "@/lib/dates.js";
import { useOptionalFeatureFlag } from "@/lib/useFeatureFlag";

import { useBreakpoints } from "@/composables/useBreakpoints";

/**
 * The phone-sheet presentation shared by the two date pickers.
 *
 * `CalendarDatepicker` (Seranova) and `components/Datepicker.vue` (legacy) wrap
 * the same `vue-datepicker-next` input and need the same answer to "is this a
 * sheet or a popover, and how does a value picked in the sheet get back into
 * the model". The rules below are each a fix for a specific reported bug, so a
 * second copy would drift the first time one of them is corrected.
 *
 * The caller keeps ownership of its own `date` model: it passes `setDate` and
 * `clearDate` rather than handing over a ref, because the two pickers store the
 * value differently (a `computed` with a setter vs. an Options API `computed`).
 */

// Loaded only when a sheet is actually opened, so the desktop path does not
// carry the drawer, the button and the range machinery it never mounts.
export const DatepickerSheetAsync = defineAsyncComponent(
  () => import("@/components/Seranova/Datepicker/DatepickerSheet.vue")
);

const MOBILE_SHEET_FLAG = "mobile_web_optimization";

/**
 * The subset of either picker's props this needs. Read through the live props
 * object so the computeds below stay reactive in both a `<script setup>` and an
 * Options API `setup(props)` host.
 */
export interface DatepickerSheetHostProps {
  disabled?: boolean;
  format?: string;
  shortcuts?: boolean;
  periods?: Array<unknown> | null;
  /** Legacy opt-in. Absent (Seranova) means the sheet is always eligible. */
  mobileSheet?: boolean;
}

export interface DatepickerSheetHandlers {
  /** Write a formatted value — or `null` — into the host's date model. */
  setDate: (value: string | string[] | null) => void;
  /** Tell the host to emit `clear-date`. */
  clearDate: () => void;
}

export function useDatepickerSheet(
  props: DatepickerSheetHostProps,
  { setDate, clearDate }: DatepickerSheetHandlers
) {
  const { type } = useBreakpoints();
  // Read optionally, so a picker mounted without Vuex -- Storybook, an isolated
  // spec, a legacy host -- reports the flag off instead of throwing, and no
  // picker mount triggers a tenant fetch.
  const sheetEnabled = useOptionalFeatureFlag(MOBILE_SHEET_FLAG);

  // `mobileSheet` is how the legacy picker stays opt-in: it reaches 17 call
  // sites this ticket is not converting, and AC4 keeps their desktop
  // presentation unchanged. A host that never declares the prop (Seranova) is
  // eligible wherever the flag and the width allow.
  const optedIn = computed(() => props.mobileSheet !== false);

  // `type` (viewport WIDTH below md), not `isHandheld` (shorter side below md).
  // isHandheld calls any window under 853px tall handheld regardless of width,
  // so an ordinary desktop browser -- a 1440x900 laptop is roughly 800px of
  // viewport once chrome is subtracted -- got the phone sheet. Matches
  // useSheetPlacement and useMobileWebOptimization, which gate this same flag
  // on width.
  //
  // The cost is a phone in landscape (863x360) counting as desktop. That is the
  // lesser harm: the acceptance criterion is about phone WIDTH, and the opposite
  // error hands the sheet to a large share of desktop users.
  const isSheet = computed(
    () => optedIn.value && sheetEnabled.value && type.value === "mobile"
  );

  // Doubles as the presentation latch: once open, the sheet stays a sheet until
  // it closes, so a rotation mid-pick cannot swap it for the popover under the
  // user's finger. The library input is rendered in both branches, so nothing
  // visibly changes if the flag resolves after first paint.
  const sheetOpen = ref(false);

  // Remounts the library input whenever the presentation flips, to discard the
  // popup state it keeps internally.
  //
  // `:open="false"` only pins the popup shut; it does not clear the library's
  // own `popupVisible`, which the tap that opened the sheet has already set
  // true. Releasing `:open` back to `undefined` returns the component to
  // uncontrolled and that retained `true` takes effect, so on an iPad a
  // portrait-to-landscape rotation raised the popover over the form with
  // nothing touched. The state is private to the library, so a fresh mount is
  // the only reset available.
  //
  // `date` is a computed over `modelValue`, so remounting cannot lose the
  // value, and this only fires when the viewport crosses the md breakpoint.
  const pickerMode = computed(() => (isSheet.value ? "sheet" : "popover"));

  const sheetPeriods = computed<DatePeriod[] | null>(() =>
    props.shortcuts && props.periods?.length
      ? (props.periods as DatePeriod[])
      : null
  );

  /**
   * The library pins its popup shut while `open` is false but still reports the
   * attempt, so this fires on the tap or focus that would have opened it.
   */
  function onRequestOpen(open: boolean) {
    if (!open || !isSheet.value || props.disabled) return;

    // Drop focus before the sheet mounts. The drawer remembers whatever was
    // focused when it opened and restores it on close -- and what is focused
    // here is the picker input, whose focus handler is the very thing that asks
    // to open. Leaving it focused makes closing the sheet immediately reopen
    // it, so neither the close button nor Apply can dismiss it.
    (document.activeElement as HTMLElement | null)?.blur();

    sheetOpen.value = true;
  }

  // Formatted here, matching the string the library would have produced, so the
  // value takes the same path into the host's model either way. The sheet deals
  // in Date objects; every consumer downstream of the model deals in strings.
  const formatForModel = (value: Date): string =>
    formatLocalDateOnly(value, props.format);

  function onSheetApply(value: Date | Date[] | null) {
    if (value === null) {
      setDate(null);
      clearDate();
      return;
    }

    setDate(
      Array.isArray(value) ? value.map(formatForModel) : formatForModel(value)
    );
  }

  return {
    isSheet,
    sheetOpen,
    pickerMode,
    sheetPeriods,
    onRequestOpen,
    onSheetApply
  };
}
